/**
 * MultiSelect — THE multi-value select for this app.
 *
 * Why it lives here and not in `@v0y4ge/jrni-ui`: the design system ships no
 * multi-select, and `jrni-ui` must stay byte-identical in every drop (standing
 * PASS criterion in CLAUDE.md). So this is a CD-owned sibling of jrni-ui's
 * `Dropdown` — deliberately built on the SAME `--jrni-*` custom properties, the
 * same 40px trigger, the same menu geometry and the same keyboard contract, so
 * the two read as one family. If the DS ever ships its own, this is a drop-in
 * shape to migrate from (`values`/`onChange` mirror `value`/`onChange`).
 *
 * What it is NOT: a combobox with type-ahead filtering, or a tag input that
 * creates new values. Both are different controls; a `filterable` option list
 * is a reasonable future addition here, free-text creation is not.
 *
 * Accessibility: `role="listbox"` + `aria-multiselectable`, one `role="option"`
 * per row with `aria-selected`, `aria-activedescendant` tracking the focused
 * row. Space/Enter toggles WITHOUT closing (the defining difference from
 * single-select), Escape closes, Backspace removes the last chip.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";

export interface MultiSelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
  /** Optional supporting text under the label. */
  supportingText?: ReactNode;
  /** Visually muted and unpickable. Already-selected disabled values stay removable. */
  disabled?: boolean;
}

export interface MultiSelectProps<TValue extends string = string> {
  options: MultiSelectOption<TValue>[];
  /** Selected values, in the order the user picked them (controlled). */
  values: TValue[];
  onChange: (values: TValue[]) => void;
  placeholder?: string;
  label?: ReactNode;
  helperText?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  /** Cap the number of selections. Unpicked options disable at the cap. */
  maxSelected?: number;
  /**
   * Above this many selections the trigger collapses from chips to an
   * "N selected" summary, so a long list can't grow the row without bound.
   */
  chipLimit?: number;
  /** Show "Select all" / "Clear" in the menu header. Off by default. */
  bulkActions?: boolean;
  /** Render the menu in a portal — use inside scrolling/overflow containers. */
  portal?: boolean;
  style?: CSSProperties;
  "aria-label"?: string;
  testid?: string;
}

const RADIUS = "var(--jrni-radius-sm)";
const C = {
  white: "var(--jrni-color-base-white)",
  n1: "var(--jrni-color-neutral-1)",
  n2: "var(--jrni-color-neutral-2)",
  n3: "var(--jrni-color-neutral-3)",
  n4: "var(--jrni-color-neutral-4)",
  n6: "var(--jrni-color-neutral-6)",
  borderStrong: "var(--jrni-color-surface-border-strong)",
  p1: "var(--jrni-color-primary-1)",
  p3: "var(--jrni-color-primary-3)",
  p4: "var(--jrni-color-primary-4)",
  red1: "var(--jrni-color-semantic-red-1)",
  focus: "var(--jrni-color-utility-focus)",
  font: "var(--jrni-font-family-sans)",
  fsP1: "var(--jrni-font-size-P1)",
  fsL1: "var(--jrni-font-size-L1)",
  fsH1: "var(--jrni-font-size-H1)",
};

export function MultiSelect<TValue extends string = string>({
  options,
  values,
  onChange,
  placeholder,
  label,
  helperText,
  error,
  disabled = false,
  maxSelected,
  chipLimit = 3,
  bulkActions = false,
  portal = false,
  style,
  "aria-label": ariaLabel,
  testid,
}: MultiSelectProps<TValue>) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  const reactId = useId();
  const triggerId = `cd-multiselect-${reactId}`;
  const listboxId = `cd-multiselect-${reactId}-listbox`;
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const selectedSet = useMemo(() => new Set<string>(values), [values]);
  const atCap = maxSelected !== undefined && values.length >= maxSelected;

  /** Selected options in *selection* order, so chips reflect what the user did. */
  const selectedOptions = useMemo(
    () => values.map((v) => options.find((o) => o.value === v)).filter((o): o is MultiSelectOption<TValue> => !!o),
    [values, options],
  );

  const isPickable = useCallback(
    (opt: MultiSelectOption<TValue>) => !opt.disabled && (!atCap || selectedSet.has(opt.value)),
    [atCap, selectedSet],
  );

  useLayoutEffect(() => {
    if (!portal || !open) return;
    const measure = () => {
      const el = triggerRef.current;
      if (el) setTriggerRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [portal, open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  function toggle(value: TValue) {
    if (selectedSet.has(value)) onChange(values.filter((v) => v !== value));
    else if (!atCap) onChange([...values, value]);
  }

  function toggleByIndex(index: number) {
    const opt = options[index];
    if (!opt || !isPickable(opt)) return;
    toggle(opt.value);
  }

  function nextPickableIndex(start: number, dir: 1 | -1): number {
    if (options.length === 0) return start;
    let i = start;
    for (let step = 0; step < options.length; step++) {
      i = (i + dir + options.length) % options.length;
      if (isPickable(options[i]!)) return i;
    }
    return start;
  }

  function handleTriggerKey(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (event.key === " " || event.key === "Enter" || event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setFocusIndex((i) => nextPickableIndex(i - 1, 1));
      return;
    }
    if (event.key === "Backspace" && values.length) {
      event.preventDefault();
      onChange(values.slice(0, -1));
    }
  }

  function handleListKey(event: ReactKeyboardEvent<HTMLUListElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusIndex((i) => nextPickableIndex(i, 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusIndex((i) => nextPickableIndex(i, -1));
      return;
    }
    // The defining difference from Dropdown: toggling does NOT close the menu.
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleByIndex(focusIndex);
      return;
    }
    if (event.key === "Backspace" && values.length) {
      event.preventDefault();
      onChange(values.slice(0, -1));
      return;
    }
    if (event.key === "Tab") setOpen(false);
  }

  const state: "default" | "error" | "disabled" | "open" = disabled
    ? "disabled"
    : error !== undefined
    ? "error"
    : open
    ? "open"
    : "default";

  const restingShadow = "var(--jrni-shadow-chrome)";
  const triggerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    width: "100%",
    minHeight: 40,
    padding: selectedOptions.length && values.length <= chipLimit ? "5px 10px" : "0 12px",
    borderRadius: RADIUS,
    background: state === "disabled" ? C.n6 : C.white,
    border:
      state === "error"
        ? `1px solid ${C.red1}`
        : state === "open"
        ? `1px solid ${C.p1}`
        : `1px solid ${C.borderStrong}`,
    color: state === "disabled" ? C.n3 : C.n1,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: C.font,
    fontSize: C.fsP1,
    fontWeight: 500,
    textAlign: "left",
    boxSizing: "border-box",
    boxShadow:
      state === "disabled"
        ? "none"
        : state === "open"
        ? `${restingShadow}, 0 0 0 1px ${C.focus}`
        : restingShadow,
    transition: "border-color 120ms, box-shadow 120ms",
  };

  const summary = `${values.length} ${t("shell.multiSelect.selected", "selected")}`;

  return (
    <div
      ref={containerRef}
      data-cd-component="multiselect"
      data-cd-state={state}
      style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative", fontFamily: C.font, ...style }}
    >
      {label !== undefined ? (
        <label htmlFor={triggerId} style={{ fontSize: C.fsH1, fontWeight: 500, color: C.n1, lineHeight: 1.4 }}>
          {label}
        </label>
      ) : null}

      {/* ui-raw: role="combobox" trigger (aria-haspopup/expanded/controls, active-descendant).
          Bigger finding: this whole file duplicates jrni-ui's MultiSelect — it is a FORK to
          delete, not a button to migrate. Listed for the select pass. */}
      {/* ui-raw: trigger of the LOCAL MultiSelect fork — scheduled for deletion at the select pass (group 3); jrni-ui ships MultiSelect, Combobox and TagInput */}
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        aria-invalid={error !== undefined ? true : undefined}
        disabled={disabled}
        data-testid={testid}
        style={triggerStyle}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleTriggerKey}
      >
        {values.length === 0 ? (
          <span style={{ color: C.n3, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {placeholder ?? t("shell.multiSelect.placeholder", "Select…")}
          </span>
        ) : values.length > chipLimit ? (
          <span style={{ flex: 1 }}>{summary}</span>
        ) : (
          <span style={{ display: "flex", flexWrap: "wrap", gap: 6, flex: 1, minWidth: 0 }}>
            {selectedOptions.map((opt) => (
              <span
                key={opt.value}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  maxWidth: "100%",
                  padding: "3px 6px 3px 8px",
                  borderRadius: RADIUS,
                  background: C.p3,
                  color: C.n1,
                  fontSize: C.fsL1,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</span>
                {/* A <span role="button"> because the trigger is already a
                    <button> — nesting real buttons is invalid HTML. */}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`${t("shell.multiSelect.remove", "Remove")} ${opt.label}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!disabled) onChange(values.filter((v) => v !== opt.value));
                  }}
                  style={{ display: "inline-flex", alignItems: "center", color: C.n2, cursor: disabled ? "not-allowed" : "pointer" }}
                >
                  <Icon.x size={11} />
                </span>
              </span>
            ))}
          </span>
        )}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {values.length > chipLimit && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label={t("shell.multiSelect.clearAll", "Clear all")}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              style={{ display: "inline-flex", alignItems: "center", color: C.n2, cursor: "pointer" }}
            >
              <Icon.x size={12} />
            </span>
          ) : null}
          <Icon.chev size={16} dir={open ? "up" : "down"} />
        </span>
      </button>

      {open
        ? (() => {
            const usePortal = portal && triggerRect && typeof document !== "undefined";
            const base: CSSProperties = {
              zIndex: usePortal ? 9500 : 100,
              margin: 0,
              padding: 4,
              listStyle: "none",
              background: C.white,
              border: `1px solid ${C.n4}`,
              borderRadius: RADIUS,
              boxShadow: "var(--jrni-shadow-lg)",
              maxHeight: 320,
              overflowY: "auto",
            };
            const menuStyle: CSSProperties = usePortal
              ? { ...base, position: "fixed", top: triggerRect!.bottom + 4, left: triggerRect!.left, width: triggerRect!.width }
              : { ...base, position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0 };

            const menu = (
              <ul
                id={listboxId}
                role="listbox"
                aria-multiselectable="true"
                aria-activedescendant={`${listboxId}-opt-${focusIndex}`}
                tabIndex={-1}
                autoFocus
                onKeyDown={handleListKey}
                style={menuStyle}
              >
                {bulkActions ? (
                  <li style={{ display: "flex", gap: 8, padding: "4px 6px 8px", borderBottom: `1px solid ${C.n4}`, marginBottom: 4 }}>
                    <span
                      role="button"
                      tabIndex={-1}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const pickable = options.filter((o) => !o.disabled).map((o) => o.value);
                        onChange(maxSelected === undefined ? pickable : pickable.slice(0, maxSelected));
                      }}
                      style={{ fontSize: C.fsL1, fontWeight: 600, color: C.p1, cursor: "pointer" }}
                    >
                      {t("shell.multiSelect.selectAll", "Select all")}
                    </span>
                    <span
                      role="button"
                      tabIndex={-1}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onChange([]);
                      }}
                      style={{ fontSize: C.fsL1, fontWeight: 600, color: C.n2, cursor: "pointer" }}
                    >
                      {t("shell.multiSelect.clear", "Clear")}
                    </span>
                  </li>
                ) : null}

                {options.map((opt, idx) => {
                  const isSelected = selectedSet.has(opt.value);
                  const isFocused = idx === focusIndex;
                  const blocked = !isPickable(opt);
                  return (
                    <li
                      key={opt.value}
                      id={`${listboxId}-opt-${idx}`}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={blocked || undefined}
                      onMouseEnter={() => !blocked && setFocusIndex(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (blocked) return;
                        toggleByIndex(idx);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        borderRadius: RADIUS,
                        background: blocked ? C.white : isFocused ? C.p4 : "transparent",
                        color: blocked ? C.n3 : C.n1,
                        cursor: blocked ? "not-allowed" : "pointer",
                        fontFamily: C.font,
                        fontSize: C.fsP1,
                        lineHeight: 1.4,
                      }}
                    >
                      {/* Square, not a tick: the shape is what tells you this
                          list is multi-select before you click anything. */}
                      <span
                        aria-hidden="true"
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isSelected ? C.p1 : C.white,
                          border: isSelected ? `1px solid ${C.p1}` : `1px solid ${C.n4}`,
                          color: C.white,
                        }}
                      >
                        {isSelected ? <Icon.check size={11} /> : null}
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</span>
                        {opt.supportingText !== undefined ? (
                          <span style={{ fontSize: C.fsL1, color: blocked ? C.n3 : C.n2 }}>{opt.supportingText}</span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            );
            return usePortal ? createPortal(menu, document.body) : menu;
          })()
        : null}

      {error !== undefined ? (
        <span role="alert" style={{ fontSize: C.fsL1, color: C.red1, lineHeight: 1.4 }}>
          {error}
        </span>
      ) : helperText !== undefined ? (
        <span style={{ fontSize: C.fsL1, color: C.n2, lineHeight: 1.4 }}>{helperText}</span>
      ) : atCap ? (
        <span style={{ fontSize: C.fsL1, color: C.n2, lineHeight: 1.4 }}>
          {`${t("shell.multiSelect.maxReached", "Maximum reached")} (${maxSelected})`}
        </span>
      ) : null}
    </div>
  );
}
